import type { ExpressTier } from 'data-types';

export type ExpressUpsellVariant = 'uber_2h' | 'same_day' | 'next_day';

export interface ExpressUpsellConfig {
  priceCents: number;
  hasCountdown: boolean;
  hasNewLabel: boolean;
}

export const UPSELL_CONFIGS: Record<ExpressUpsellVariant, ExpressUpsellConfig> = {
  same_day: {
    priceCents: 590,
    hasCountdown: true,
    hasNewLabel: false,
  },
  next_day: {
    priceCents: 590,
    hasCountdown: false,
    hasNewLabel: true,
  },
  uber_2h: {
    priceCents: 1090,
    hasCountdown: false,
    hasNewLabel: false,
  },
};

/**
 * Priority: Uber → Same-day → Next-day (SPEC section 13).
 * Returns the highest-priority available tier, or null if none.
 */
export const resolveExpressUpsellVariant = (
  tiers: ExpressTier[],
): ExpressUpsellVariant | null => {
  const available = tiers.filter(t => t.isAvailable && t.isCutoffPassed !== true);
  if (available.some(t => t.tier === 'uber_2h')) return 'uber_2h';
  if (available.some(t => t.tier === 'same_day')) return 'same_day';
  if (available.some(t => t.tier === 'next_day')) return 'next_day';
  return null;
};

/**
 * Guards the express upsell popup against carts the quoted express stock can't fulfill.
 *
 * Address-in-range tells us express *reaches* the customer; it does NOT tell us the
 * customer's products are stocked in an express-capable stock. The server enforces this
 * second axis in validateExpressDelivery (OrderManagerV2): any product required for express
 * that isn't parented in the express stock throws `missing_product_for_express`. We mirror
 * that here so the popup never offers express for an uncoverable cart (HL-9096 root cause:
 * product 90337 lived only in cannabis_bayreuth, quote ran on Spital_Apotheke → 500).
 *
 * @param cartProductItemIDs    every product_item_id currently in the cart
 * @param expressCoveredItemIDs product_item_ids stocked in the quoted express stock
 */
export const doesExpressStockCoverCart = (
  cartProductItemIDs: number[],
  expressCoveredItemIDs: number[],
): boolean => {
  // No express stock matched the cart (out of range / unpinned) → never show. Guards the
  // HL-9096 root cause directly: empty coverage was the uncoverable-cart 500.
  if (expressCoveredItemIDs.length === 0) return false;

  // All-or-nothing: mirror the server's strict per-product check (validateExpressDelivery
  // throws missing_product_for_express if ANY required product isn't parented in the express
  // stock). Express has no split-fulfillment path — a single parent stock ships the order —
  // so every cart product must be coverable, else the order POST 500s.
  const covered = new Set(expressCoveredItemIDs);
  return cartProductItemIDs.every(id => covered.has(id));
};
